/** kiosk: ChromeOS kiosk setup. English + Arabic (Arabic is a draft for native review). */
const en = {
  title: 'Set up a ChromeOS screen',
  subtitle: 'Run Displaro as a kiosk app: it starts by itself, full screen, with no browser around it.',
  back: 'Players',
  link: 'ChromeOS setup',
  cardTitle: 'Kiosk mode (ChromeOS)',
  cardOn: 'This screen runs Displaro as a kiosk app: it starts by itself after power-on.',
  cardOff:
    'This screen runs Displaro in a browser tab: it won’t start by itself after power-on, and the browser can be closed.',
  showSteps: 'Show the kiosk setup',
  hideSteps: 'Hide the kiosk setup',
  kioskOn: 'Kiosk',
  kioskOff: 'Browser tab',
  why: 'ChromeOS only allows kiosk mode on devices your organization manages, and it is turned on in the Google Admin console, not on the screen. Displaro can’t switch it on by itself.',
  needTitle: 'You need',
  need1: 'A ChromeOS device: a Chromebox, Chromebase, or a PC with ChromeOS Flex.',
  need2: 'A Chrome Enterprise or Education upgrade licence for it (often included with signage Chromeboxes).',
  need3: 'Access to your organization’s Google Admin console (admin.google.com).',
  urlTitle: 'The kiosk app address',
  urlHint: 'Paste this into the Admin console in step 2.',
  copy: 'Copy',
  copied: 'Copied',
  appsLabel: 'Custom apps address (optional)',
  appsHint:
    'The separate address your server uses for custom HTML5 apps and compositions. Leave empty if it has none: those then show as a placeholder.',
  appsBad: 'Enter an address starting with https://, different from this one.',
  stepsTitle: 'Steps',
  s1: 'Enroll the device in your Google Admin console (on the device’s first start: Enterprise enrollment, Ctrl+Alt+E).',
  s2: 'In the Admin console go to Devices → Chrome → Apps & extensions → Kiosks, add an app by URL and paste the address above.',
  s3: 'Set it to auto-launch, so the device starts straight into Displaro.',
  s4: 'Allow autoplay for it (Chrome policy “Autoplay allowed”), or videos and the audio track play silently.',
  s5: 'Restart the device. Displaro starts full screen and shows a pairing code: pair it from Players → New player.',
  afterTitle: 'Once it runs',
  after1: 'After its first pairing it starts without network, from the files it keeps.',
  after2:
    'Restart app works from the CMS; reboot and whole-screen screenshots don’t (ChromeOS doesn’t let apps do them). Live View shows Displaro’s own output.',
};
const ar: typeof en = {
  title: 'إعداد شاشة ChromeOS',
  subtitle: 'شغّل Displaro كتطبيق كشك: يبدأ تلقائيًا بملء الشاشة دون أي متصفح حوله.',
  back: 'المشغلات',
  link: 'إعداد ChromeOS',
  cardTitle: 'وضع الكشك (ChromeOS)',
  cardOn: 'تشغّل هذه الشاشة Displaro كتطبيق كشك: يبدأ تلقائيًا بعد التشغيل.',
  cardOff: 'تشغّل هذه الشاشة Displaro في علامة تبويب متصفح: لن يبدأ تلقائيًا بعد التشغيل، ويمكن إغلاق المتصفح.',
  showSteps: 'عرض إعداد الكشك',
  hideSteps: 'إخفاء إعداد الكشك',
  kioskOn: 'كشك',
  kioskOff: 'علامة تبويب',
  why: 'لا يسمح ChromeOS بوضع الكشك إلا على الأجهزة التي تديرها مؤسستك، ويُفعَّل من وحدة تحكم مشرف Google وليس من الشاشة. لا يستطيع Displaro تفعيله بنفسه.',
  needTitle: 'تحتاج إلى',
  need1: 'جهاز ChromeOS: ‏Chromebox أو Chromebase أو حاسوب يعمل بـ ChromeOS Flex.',
  need2: 'ترخيص ترقية Chrome Enterprise أو Education له (غالبًا ما يكون مضمّنًا مع أجهزة Chromebox للافتات).',
  need3: 'صلاحية الوصول إلى وحدة تحكم مشرف Google لمؤسستك (admin.google.com).',
  urlTitle: 'عنوان تطبيق الكشك',
  urlHint: 'الصقه في وحدة تحكم المشرف في الخطوة 2.',
  copy: 'نسخ',
  copied: 'تم النسخ',
  appsLabel: 'عنوان التطبيقات المخصصة (اختياري)',
  appsHint:
    'العنوان المنفصل الذي يستخدمه خادمك لتطبيقات HTML5 المخصصة والتراكيب. اتركه فارغًا إن لم يوجد: ستظهر حينها كعنصر نائب.',
  appsBad: 'أدخل عنوانًا يبدأ بـ https://‏ ويختلف عن هذا العنوان.',
  stepsTitle: 'الخطوات',
  s1: 'سجّل الجهاز في وحدة تحكم مشرف Google (عند أول تشغيل للجهاز: تسجيل المؤسسة، Ctrl+Alt+E).',
  s2: 'في وحدة تحكم المشرف انتقل إلى الأجهزة ← Chrome ← التطبيقات والإضافات ← الأكشاك، وأضف تطبيقًا بعنوان URL والصق العنوان أعلاه.',
  s3: 'اضبطه على التشغيل التلقائي ليبدأ الجهاز مباشرة في Displaro.',
  s4: 'اسمح له بالتشغيل التلقائي (سياسة Chrome ‏“Autoplay allowed”)، وإلا تُشغَّل الفيديوهات والمسار الصوتي دون صوت.',
  s5: 'أعد تشغيل الجهاز. يبدأ Displaro بملء الشاشة ويعرض رمز إقران: أقرنه من المشغلات ← مشغل جديد.',
  afterTitle: 'بعد بدء التشغيل',
  after1: 'بعد أول إقران يبدأ دون شبكة من الملفات التي يحتفظ بها.',
  after2:
    'تعمل إعادة تشغيل التطبيق من نظام إدارة المحتوى؛ أما إعادة تشغيل الجهاز ولقطات الشاشة الكاملة فلا (لا يسمح ChromeOS للتطبيقات بذلك). يعرض العرض المباشر مخرجات Displaro نفسه.',
};
export default { en, ar };
